import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useProjectElementListStore } from '../../shared/store/projectElementStore';
import { DisplayAlignment, DisplaySize, ImageStyle, ModalType } from '../../shared/enum/EnumRepository';
import { convertWorkSizeToString, PEWorkData } from '../../shared/dto/EntityRepository';
import { StyledImgProps } from '../../shared/dto/StyleCompRepository';
import { convertS3UrlToCloudFrontUrl } from '../../shared/aws/awsHelper';
import OptimizedMoleculeImg from '../../shared/component/molecule/OptimizedMoleculeImg';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import fullIcon from '../../asset/icon/full_image.png';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useEditMode } from '../../shared/hooks/useEditMode';
import {
  getCompactLandscapeWidth,
  getCompactPortraitHeight,
  getDisplaySizeValue,
  PROJECT_IMAGE_SIZE
} from '../../shared/util/displayImageSize';
import { useSettingStore } from '../../shared/store/settingStore';
import { resolveWorkArtistName, shouldShowWorkArtist } from '../../shared/util/workArtist';

export interface WorkProps {
  peId: string;
  alignment: DisplayAlignment;
  displaySize: DisplaySize;
  data: PEWorkData;
  priority: boolean;
  imageStyle: ImageStyle;
}

const WorkImgInPe: React.FC<WorkProps> = ({ peId, alignment, displaySize, data, priority, imageStyle }) => {
  const { isEditMode } = useEditMode();
  const handleImageChange = useProjectElementListStore((s) => s.updateImage);
  const { setStandardModal } = useModalStore();
  const setting = useSettingStore((state) => state.setting);
  const [isHovered, setIsHovered] = useState(false);
  
  const convertedStringSize = convertWorkSizeToString(data.size, data.dimensionsType, data.sizeText);
  const convertedCloudFrontUrl = convertS3UrlToCloudFrontUrl(data.uploadFile.originUrl);
  const imageRatio = data.uploadFile?.width && data.uploadFile?.height
    ? data.uploadFile.width / data.uploadFile.height
    : undefined;

  const getStyledImage = () => {
    if (imageStyle === ImageStyle.LANDSCAPE) {
      return WorkImageForLandscape as React.ComponentType<StyledImgProps>;
    } else {
      return WorkImageForPortrait as React.ComponentType<StyledImgProps>;
    }
  };

  const showOriginImg = () => {
    const artistName = shouldShowWorkArtist(setting)
      ? resolveWorkArtistName(data.artist)
      : '';
    setStandardModal({
      modalType: ModalType.ORIGIN_IMG,
      title: [artistName, data.title, data.material, convertedStringSize, data.prodYear]
        .filter(Boolean)
        .join(', ') || null,
      value: convertedCloudFrontUrl,
      handleChange: () => { },
      option: data.uploadFile.allowDownload
    });
  }

  return (
    <ImgInPeWrapper
      $alignment={alignment || DisplayAlignment.CENTER}
      $displaySize={displaySize || DisplaySize.BIG}
      $imageStyle={imageStyle || ImageStyle.LANDSCAPE}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      {!isEditMode && (
        <BtnContainer2 $isHovered={isHovered}>
          <UniversalIconBtn 
            size="fit" 
            border={true} 
            backgroundImg={fullIcon} 
            onClick={showOriginImg} />
        </BtnContainer2>
      )}
      <OptimizedMoleculeImg
        srcUrl={convertedCloudFrontUrl}
        alt={data.title}
        displaySize={displaySize}
        displayAlignment={alignment || DisplayAlignment.CENTER}
        handleChange={(originUrl: string, width: number, height: number) => handleImageChange(
          peId,
          originUrl,
          width,
          height
        )}
        StyledImg={getStyledImage()}
        priority={priority}
        imageRatio={imageRatio}
      />
    </ImgInPeWrapper>
  );
};

export const ImgInPeWrapper = styled.div<{ $alignment: DisplayAlignment, $displaySize: DisplaySize, $imageStyle: ImageStyle }>`
  height: fit-content;
//landscape에 wide면 margin 없음

//alignment가 left면 margin-right: 1vw;
//alignment가 right면 margin-left: 1vw;
  margin: ${({ $alignment, $displaySize, $imageStyle }) => {
    // No margin if landscape and wide
    if ($imageStyle === ImageStyle.LANDSCAPE && $displaySize === DisplaySize.WIDE) {
      return '0';
    }
    // Add margin based on alignment
    if ($alignment === DisplayAlignment.LEFT_FLUSH || $alignment === DisplayAlignment.LEFT_OFFSET) {
      return '0 0.5vw 0 0';
    }
    if ($alignment === DisplayAlignment.RIGHT_FLUSH || $alignment === DisplayAlignment.RIGHT_OFFSET) {
      return '0 0 0 0.5vw';
    }
    return '0';
  }};

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 0;
`;

export const WorkImageForPortrait = styled.img<{ $displaySize: DisplaySize, $imageRatio?: number }>`
  height: ${({ $displaySize, $imageRatio }) =>
    getCompactPortraitHeight($displaySize, $imageRatio, PROJECT_IMAGE_SIZE)};
  width: auto;
  max-width: ${({ $displaySize }) =>
    `${getDisplaySizeValue($displaySize, PROJECT_IMAGE_SIZE.widthVw)}vw`};
  transition: height 0.6s ease, max-width 0.6s ease;
  object-fit: contain;
`;

export const WorkImageForLandscape = styled.img<{ $displaySize: DisplaySize, $imageRatio?: number }>`
  width: ${({ $displaySize, $imageRatio }) =>
    getCompactLandscapeWidth($displaySize, $imageRatio, PROJECT_IMAGE_SIZE)};
  height: auto;
  max-height: ${({ $displaySize }) =>
    `${getDisplaySizeValue($displaySize, PROJECT_IMAGE_SIZE.heightVh)}vh`};
  transition: width 0.6s ease, max-height 0.6s ease;
  object-fit: contain;
`;

const BtnContainer2 = styled.div<{ $isHovered: boolean }>`
  position: absolute;
  right: 0.5vh;
  top: 0.5vh;
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vh;
  opacity: ${({ $isHovered }) => $isHovered ? 1 : 0};
  cursor: ${({ $isHovered }) => $isHovered ? 'pointer' : 'none'};
  pointer-events: ${({ $isHovered }) => $isHovered ? 'auto' : 'none'};
  transition: all 0.3s ease-in-out;
`

export default WorkImgInPe;
